/**
 * framylo - Frame Configurator v2: two colour zones backed by REAL variants.
 *
 * The product has two options, "Shutters" and "Frame" (Blue, Yellow, Red,
 * Green, Black, White: 36 variants). The section renders a JSON map
 * ([data-variant-map]: "blue|white" -> {id, cents, price, ok, s, f, img}), two
 * swatch rows ([data-swatch][data-row="shutters"|"frame"]), the main image and
 * the crown name overlay.
 *
 * Handles:
 *  - A pick -> the pair's variant id, price, catalogue image (src, srcset, alt),
 *    the "your frame" thumb, the row names and the name letter colour.
 *  - Initial pair, first match wins: ?shutters=&frame= (a missing or unknown
 *    side takes the default pair's side) -> ?color= (retired shade handles
 *    through LEGACY_COLOR; a plain colour handle c gives c|c) -> ?variant=, only
 *    when the URL has it (data-selected-variant; never the hidden input, which
 *    holds the server's pair) -> the section default pair -> the first map entry.
 *    Every pick rewrites ?shutters=&frame= with history.replaceState (other
 *    params kept, color and variant dropped).
 *  - Preload: once the main image has loaded, at idle, the 10 neighbours (same
 *    shutters with the 5 other frames, same frame with the 5 other shutters) at
 *    the width the browser uses; the exact pair on swatch hover or focus. Off
 *    when the visitor asked to save data.
 *  - Empty map (no Shutters/Frame options, e.g. the old product) or an
 *    unavailable pair: both buttons stay locked, so the page never sells a
 *    colour nobody picked. A picked pair that is sold out (or has no variant)
 *    also fills the [data-pair-unavailable] status line, so the visitor sees why.
 *  - One frame per add: Buy now posts the form (selected variant, quantity 1)
 *    to cart/add.js, then goes to checkout; Add to cart is the plain form post.
 *    Both buttons lock while an add is on its way, so a double tap cannot add
 *    a second frame; they unlock when Back restores the page from the bfcache,
 *    or after UNLOCK_MS if the add hangs or its navigation was cancelled.
 *  - Thumbnails: "your frame" (the live pair) <-> lifestyle photos; a photo
 *    hides the crown name.
 *  - Name add-on: "add a name" adds a second line (the add-on product, whose
 *    price is the name price) carrying Name and Frame colour ("Shutters Blue /
 *    Frame White") as properties; both lines go in one cart/add.js call. Up to
 *    10 capitals A-Z and spaces, shown live on the crown band and in the pill.
 *    The crown name is sized in cqw (5.8 mm capitals = 2.83 % of the preview
 *    width); a browser without container units gets the same size in px
 *    through --crown-px.
 */

// Safety unlock for a hung add (fetch has no timeout) or a cancelled
// navigation (Stop, or Back before it commits). It only re-enables the
// buttons and never posts again: a re-post could add a second frame.
const UNLOCK_MS = 12000;
// Shown on the tapped button while its add is on its way (the CSS uppercases it).
const BUSY_LABEL = 'adding...';
const ROWS = ['shutters', 'frame'];
// Catalogue renders are 1600x1600; the CDN serves these widths.
const WIDTHS = [800, 1200, 1600];
const THUMB_WIDTH = 240;
const LIGHT_INK = '#ffffff';
const DARK_INK = '#1b1b1b';
// The made name never goes below 4.5 mm capitals (5.8 mm is the normal size).
// The crown's base size is the made 5.8 mm, so this ratio is 4.5 mm.
const MIN_CROWN_FIT = 4.5 / 5.8;
// The crown font size as a share of the preview width (the CSS 2.83cqw).
const CROWN_EM = 0.0283;
// Safari 15 and older, Chrome before 105: no cqw, so the script sizes the crown.
const HAS_CQ = Boolean(window.CSS && CSS.supports && CSS.supports('width: 1cqw'));
// Retired one-colour shade handles (old ads, cart links, homepage cards) -> pair.
const LEGACY_COLOR = {
  'santorini-blue': 'blue|white',
  'olive-grove': 'green|white',
  terracotta: 'red|white',
  'amalfi-lemon': 'yellow|white',
  'marrakech-sand': 'white|white',
  'riviera-blue': 'blue|white'
};

class FrameConfigurator extends HTMLElement {
  connectedCallback() {
    // Every listener hangs off this controller and disconnectedCallback aborts
    // it, so an element that is moved (theme editor reorder) never binds its
    // listeners twice.
    this.ac?.abort();
    this.ac = new AbortController();

    this.form = this.querySelector('[data-cfg-form]');
    this.mainImage = this.querySelector('[data-main-image]');
    this.previewFrame = this.querySelector('[data-preview-frame]');
    this.crown = this.querySelector('[data-crown-name]');
    this.previewBox = this.crown ? this.crown.closest('.framylo-cfg__preview') : null;
    this.unavailableEl = this.querySelector('[data-pair-unavailable]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.priceEl = this.querySelector('[data-cfg-price]');
    this.buyNowBtn = this.querySelector('[data-buy-now]');
    this.addBtn = this.querySelector('[data-add-to-cart]');

    this.map = FrameConfigurator.readMap(this.querySelector('[data-variant-map]'));
    // The swatches carry the option values, so look entries up by value names.
    this.byNames = {};
    Object.values(this.map).forEach((e) => {
      if (e && e.s && e.f) this.byNames[`${e.s}|${e.f}`] = e;
    });
    this.swatches = {};
    this.rowNameEls = {};
    ROWS.forEach((row) => {
      this.swatches[row] = Array.from(this.querySelectorAll(`[data-swatch][data-row="${row}"]`));
      this.rowNameEls[row] = this.querySelector(`[data-row-name="${row}"]`);
    });
    this.state = { shutters: null, frame: null };
    this.entry = null;
    // Catalogue image URLs already requested by the preloader.
    this.preloaded = new Set();

    this.nameWrap = this.querySelector('[data-name-addon]');
    if (this.nameWrap) {
      this.nameToggle = this.nameWrap.querySelector('[data-name-toggle]');
      this.nameBody = this.nameWrap.querySelector('[data-name-body]');
      this.nameInput = this.nameWrap.querySelector('[data-name-input]');
      this.namePreview = this.nameWrap.querySelector('[data-name-preview]');
      this.nameError = this.nameWrap.querySelector('[data-name-error]');
      this.addonId = this.nameWrap.dataset.addonId;
      this.addonCents = parseInt(this.nameWrap.dataset.addonCents, 10) || 0;
    }
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.liveThumb = this.thumbs.find((t) => t.hasAttribute('data-live')) || null;
    this.liveThumbImg = this.liveThumb ? this.liveThumb.querySelector('img') : null;

    // One frame per add: pin the hidden quantity to 1 (a browser can restore
    // an older form value on reload).
    const qtyInput = this.querySelector('[data-qty-input]');
    if (qtyInput) qtyInput.value = '1';

    // Start unlocked: Firefox can restore a button's disabled state on reload.
    // Nothing is sellable until a pair with an available variant is applied
    // (an empty map never applies one, so the buttons stay locked).
    this.variantOk = false;
    this.setBusy(false);

    this.bindEvents(this.ac.signal);

    // A reload can restore the tick box and the typed name: show them consistently.
    if (this.nameToggle && this.nameBody) {
      this.nameBody.hidden = !this.nameToggle.checked;
      if (this.nameInput) this.nameInput.value = FrameConfigurator.cleanName(this.nameInput.value);
    }

    const initial = this.initialPair();
    if (initial) this.applyPair(initial[0], initial[1], { fromUrl: true });
    else this.renderName();

    // The crown is sized for Arvo: fit it again once the font has arrived.
    document.fonts?.ready?.then(() => this.fitCrown()).catch(() => {});

    // Without container units the crown's px size follows the preview width.
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    if (!HAS_CQ && this.crown && this.previewBox) {
      if (window.ResizeObserver) {
        this.resizeObs = new ResizeObserver(() => this.fitCrown());
        this.resizeObs.observe(this.previewBox);
      } else {
        window.addEventListener('resize', () => this.fitCrown(), { signal: this.ac.signal });
      }
    }
  }

  disconnectedCallback() {
    this.ac?.abort();
    this.resizeObs?.disconnect();
    clearTimeout(this.unlockTimer);
  }

  bindEvents(signal) {
    const opts = { signal };

    ROWS.forEach((row) => {
      this.swatches[row].forEach((btn) => {
        btn.addEventListener('click', () => this.pick(row, btn.dataset.handle), opts);
        // Warm the image this click would show.
        const warm = () => {
          const s = row === 'shutters' ? btn.dataset.handle : this.state.shutters;
          const f = row === 'frame' ? btn.dataset.handle : this.state.frame;
          if (s && f) this.preloadPair(s, f);
        };
        btn.addEventListener('pointerenter', warm, opts);
        btn.addEventListener('focus', warm, opts);
      });
    });

    this.thumbs.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.hasAttribute('data-live')) {
          this.showLive();
        } else if (this.mainImage && btn.dataset.full) {
          // A lifestyle photo: show it cleanly, hide the crown name
          this.previewFrame?.classList.add('is-photo');
          if (btn.dataset.srcset) this.mainImage.srcset = btn.dataset.srcset;
          else this.mainImage.removeAttribute('srcset');
          this.mainImage.src = btn.dataset.full;
          this.mainImage.alt = btn.dataset.alt || '';
          this.setPressed(this.thumbs, btn);
        }
      }, opts);
    });

    this.buyNowBtn?.addEventListener('click', () => this.buyNow(), opts);

    if (this.nameToggle) {
      this.nameToggle.addEventListener('change', () => {
        if (this.nameBody) this.nameBody.hidden = !this.nameToggle.checked;
        this.showNameError('');
        if (this.nameToggle.checked) this.showLive();
        this.renderName();
        this.updatePrice();
        if (this.nameToggle.checked) this.nameInput?.focus();
      }, opts);
      this.nameInput?.addEventListener('input', () => {
        const clean = FrameConfigurator.cleanName(this.nameInput.value);
        if (clean !== this.nameInput.value) this.nameInput.value = clean;
        // Typing a name while a photo is shown: back to the frame, to see it.
        this.showLive();
        this.renderName();
        this.showNameError('');
      }, opts);
    }

    // Add to cart is a native post: lock both buttons while it is on its way.
    this.form?.addEventListener('submit', (e) => {
      if (this.busy || !this.variantOk) {
        e.preventDefault();
        return;
      }
      if (this.nameOn()) {
        e.preventDefault();
        this.addWithName('cart', e.submitter || this.addBtn);
        return;
      }
      this.setBusy(true, e.submitter || this.addBtn);
    }, opts);

    // Back from checkout or the cart can restore this page from the bfcache
    // with the buttons still locked.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.setBusy(false);
    }, opts);
  }

  // ---- Pair selection ----------------------------------------------------
  static readMap(el) {
    try {
      const m = JSON.parse((el && el.textContent) || '{}');
      return m && typeof m === 'object' && !Array.isArray(m) ? m : {};
    } catch (e) {
      return {};
    }
  }

  swatchFor(row, handle) {
    if (!handle || !this.swatches[row]) return null;
    const h = String(handle).trim().toLowerCase();
    return this.swatches[row].find((b) => b.dataset.handle === h) || null;
  }

  entryFor(s, f) {
    const sb = this.swatchFor('shutters', s);
    const fb = this.swatchFor('frame', f);
    return sb && fb ? this.byNames[`${sb.dataset.name}|${fb.dataset.name}`] || null : null;
  }

  // [shutters handle, frame handle] when both are swatches and the pair is in the map.
  pairIfValid(s, f) {
    return this.entryFor(s, f) ? [this.swatchFor('shutters', s).dataset.handle, this.swatchFor('frame', f).dataset.handle] : null;
  }

  handlesOf(entry) {
    if (!entry) return null;
    const sb = this.swatches.shutters.find((b) => b.dataset.name === entry.s);
    const fb = this.swatches.frame.find((b) => b.dataset.name === entry.f);
    return sb && fb ? [sb.dataset.handle, fb.dataset.handle] : null;
  }

  // The section default pair, else the first map entry that has both swatches.
  defaultPair() {
    const d = this.pairIfValid(this.dataset.defaultShutters, this.dataset.defaultFrame);
    if (d) return d;
    for (const key of Object.keys(this.map)) {
      const p = this.handlesOf(this.map[key]);
      if (p) return p;
    }
    return null;
  }

  initialPair() {
    const fallback = this.defaultPair();
    if (!fallback) return null;
    const params = new URLSearchParams(window.location.search);

    // 1. ?shutters=&frame=: a missing or unknown side takes the default side.
    const ps = this.swatchFor('shutters', params.get('shutters'));
    const pf = this.swatchFor('frame', params.get('frame'));
    if (ps || pf) {
      const p = this.pairIfValid(ps ? ps.dataset.handle : fallback[0], pf ? pf.dataset.handle : fallback[1]);
      if (p) return p;
    }

    // 2. ?color=: a retired shade handle, or a plain colour for a one-colour frame.
    const color = (params.get('color') || '').trim().toLowerCase();
    if (color) {
      const legacy = LEGACY_COLOR[color];
      const p = legacy ? this.pairIfValid(...legacy.split('|')) : this.pairIfValid(color, color);
      if (p) return p;
    }

    // 3. ?variant=, only when the URL has it (cart lines, catalogue links).
    const vid = this.dataset.selectedVariant;
    if (vid) {
      const p = this.handlesOf(Object.values(this.map).find((e) => e && String(e.id) === String(vid)));
      if (p) return p;
    }

    // 4./5. The section default pair, else the first map entry.
    return fallback;
  }

  pick(row, handle) {
    const s = row === 'shutters' ? handle : this.state.shutters;
    const f = row === 'frame' ? handle : this.state.frame;
    if (s && f) this.applyPair(s, f);
  }

  applyPair(s, f, { fromUrl = false } = {}) {
    const sb = this.swatchFor('shutters', s);
    const fb = this.swatchFor('frame', f);
    if (!sb || !fb) return;
    // Picking while a lifestyle photo is shown: back to the frame, to see the pick.
    const leftPhoto = this.leavePhoto();
    const previous = this.entry;

    this.state = { shutters: sb.dataset.handle, frame: fb.dataset.handle };
    this.setPressed(this.swatches.shutters, sb);
    this.setPressed(this.swatches.frame, fb);
    if (this.rowNameEls.shutters) this.rowNameEls.shutters.textContent = sb.dataset.name || '';
    if (this.rowNameEls.frame) this.rowNameEls.frame.textContent = fb.dataset.name || '';
    this.shutterHex = sb.dataset.hex;
    this.frameHex = fb.dataset.hex;

    const entry = this.byNames[`${sb.dataset.name}|${fb.dataset.name}`] || null;
    this.entry = entry;
    // A pair with no variant (or a sold-out one) must never post the previous
    // pair's id: lock the buttons instead.
    this.variantOk = Boolean(entry && entry.ok && entry.id);
    // Say why the buttons are locked (a status line: announced when filled).
    if (this.unavailableEl) {
      const why = this.variantOk ? '' : this.unavailableEl.dataset.text || '';
      if (this.unavailableEl.textContent !== why) this.unavailableEl.textContent = why;
    }
    if (entry && this.variantInput) this.variantInput.value = entry.id;
    this.pairCents = entry ? Number(entry.cents) || 0 : 0;
    this.pairPrice = entry ? entry.price || '' : '';
    this.pairLabel = entry ? `Shutters ${entry.s} / Frame ${entry.f}` : '';
    if (entry) this.showPair(entry);
    else if (leftPhoto && previous) this.showPair(previous);

    this.renderName();
    if (!fromUrl) this.writeUrl();
    this.updatePrice();
    this.syncButtons();
    this.schedulePreload();
  }

  showPair(entry) {
    if (this.mainImage) {
      if (entry.img) {
        this.mainImage.srcset = WIDTHS.map((w) => `${FrameConfigurator.sized(entry.img, w)} ${w}w`).join(', ');
        this.mainImage.src = FrameConfigurator.sized(entry.img, WIDTHS[WIDTHS.length - 1]);
      }
      this.mainImage.alt = FrameConfigurator.altFor(entry);
    }
    if (this.liveThumbImg && entry.img) this.liveThumbImg.src = FrameConfigurator.sized(entry.img, THUMB_WIDTH);
  }

  // Leaves the lifestyle-photo state; true when a photo was shown.
  leavePhoto() {
    if (!this.previewFrame || !this.previewFrame.classList.contains('is-photo')) return false;
    this.previewFrame.classList.remove('is-photo');
    if (this.liveThumb) this.setPressed(this.thumbs, this.liveThumb);
    return true;
  }

  // Back from a lifestyle photo to the live pair (its image and crown name).
  showLive() {
    if (!this.leavePhoto()) return;
    if (this.entry) this.showPair(this.entry);
    this.fitCrown();
  }

  writeUrl() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('color');
      url.searchParams.delete('variant');
      url.searchParams.set('shutters', this.state.shutters);
      url.searchParams.set('frame', this.state.frame);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) {
      // Never block a pick over the address bar.
    }
  }

  static altFor(e) {
    return e.s === e.f
      ? `framylo window frame, all ${e.s}`
      : `framylo window frame with ${e.s} shutters and a ${e.f} frame`;
  }

  // The same CDN image at another width (image_url puts width= in the query).
  static sized(url, w) {
    if (!url) return '';
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${w}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
  }

  // ---- Preload -------------------------------------------------------------
  static saveData() {
    return Boolean(navigator.connection && navigator.connection.saveData);
  }

  // The width the browser picked from the srcset, else the one it would pick.
  preloadWidth() {
    const m = /[?&]width=(\d+)/.exec((this.mainImage && this.mainImage.currentSrc) || '');
    if (m && WIDTHS.includes(Number(m[1]))) return Number(m[1]);
    const need = ((this.mainImage && this.mainImage.clientWidth) || 0) * (window.devicePixelRatio || 1);
    return WIDTHS.find((w) => w >= need) || WIDTHS[WIDTHS.length - 1];
  }

  preloadPair(s, f, width) {
    if (FrameConfigurator.saveData()) return;
    const e = this.entryFor(s, f);
    if (!e || !e.img) return;
    const url = FrameConfigurator.sized(e.img, width || this.preloadWidth());
    if (this.preloaded.has(url)) return;
    this.preloaded.add(url);
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }

  preloadAround(s, f) {
    const w = this.preloadWidth();
    this.swatches.frame.forEach((b) => {
      if (b.dataset.handle !== f) this.preloadPair(s, b.dataset.handle, w);
    });
    this.swatches.shutters.forEach((b) => {
      if (b.dataset.handle !== s) this.preloadPair(b.dataset.handle, f, w);
    });
  }

  // After the main image has loaded, at idle: the neighbours of the current pair.
  schedulePreload() {
    if (FrameConfigurator.saveData() || !this.entry || !this.mainImage) return;
    const { shutters, frame } = this.state;
    const run = () => {
      // Only around the pair still shown (quick picks skip the ones in between).
      if (this.state.shutters === shutters && this.state.frame === frame) this.preloadAround(shutters, frame);
    };
    const idle = () => {
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 2500 });
      else window.setTimeout(run, 300);
    };
    if (this.mainImage.complete && this.mainImage.naturalWidth) idle();
    else this.mainImage.addEventListener('load', idle, { once: true, signal: this.ac.signal });
  }

  // ---- Name add-on -------------------------------------------------------
  static cleanName(v) {
    return (v || '').toUpperCase().replace(/[^A-Z ]/g, '').replace(/ {2,}/g, ' ').replace(/^ /, '').slice(0, 10);
  }

  static hex(v) {
    const m = /^#?([0-9a-f]{6})$/i.exec((v || '').trim());
    return m ? `#${m[1].toLowerCase()}` : null;
  }

  static isLight(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return false;
    const n = parseInt(m[1], 16);
    const lin = (c) => { c /= 255; return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
    const L = 0.2126 * lin(n >> 16) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
    return L > 0.45;
  }

  // Name letters: white, except on a light frame (White, Yellow), where the
  // section rule decides. "black" (the default, as made: Black letters on White
  // and Yellow frames): always near-black. "shutter": the shutter colour when the
  // shutters are dark, else near-black. Used by the crown name and the pill.
  static inkFor(shutterHex, frameHex, rule) {
    if (!FrameConfigurator.isLight(frameHex)) return LIGHT_INK;
    const s = FrameConfigurator.hex(shutterHex);
    if (rule !== 'black' && s && !FrameConfigurator.isLight(s)) return s;
    return DARK_INK;
  }

  letterInk() {
    return FrameConfigurator.inkFor(this.shutterHex, this.frameHex, this.dataset.nameDarkRule || 'black');
  }

  renderName() {
    const typed = this.nameInput ? FrameConfigurator.cleanName(this.nameInput.value).trim() : '';
    const ink = this.letterInk();
    if (this.namePreview) {
      this.namePreview.textContent = typed;
      if (this.frameHex) this.namePreview.style.setProperty('--pill-bg', this.frameHex);
      this.namePreview.style.setProperty('--name-ink', ink);
    }
    if (this.crown) {
      this.crown.textContent = this.nameOn() ? typed : '';
      this.crown.style.setProperty('--name-ink', ink);
      this.fitCrown();
    }
  }

  // A long, wide name shrinks to stay inside the crown band, like the made part
  // (never below 4.5 mm capitals).
  fitCrown() {
    const c = this.crown;
    if (!c) return;
    // No container units: the base size in px (the CSS 2.83cqw).
    if (!HAS_CQ && this.previewBox) {
      c.style.setProperty('--crown-px', `${(this.previewBox.clientWidth * CROWN_EM).toFixed(2)}px`);
    }
    c.style.removeProperty('--crown-fit');
    if (!c.textContent || !c.offsetWidth) return;
    // Sized for Arvo: measure again once it has arrived.
    if (document.fonts && !this.fontWait && !document.fonts.check('700 16px framylo-arvo')) {
      this.fontWait = document.fonts.load('700 16px framylo-arvo')
        .then(() => { this.fontWait = null; this.fitCrown(); })
        .catch(() => { this.fontWait = null; });
    }
    const range = document.createRange();
    range.selectNodeContents(c);
    // Ink width: the text minus the letter-spacing that follows the last letter.
    const ink = () => range.getBoundingClientRect().width - (parseFloat(getComputedStyle(c).letterSpacing) || 0);
    const room = c.clientWidth;
    // Small sizes round the glyph widths, so the width is not quite proportional
    // to the size: a few proportional steps settle it.
    let fit = 1;
    for (let i = 0; i < 4; i += 1) {
      const need = ink();
      if (need <= room + 0.5 || fit <= MIN_CROWN_FIT) break;
      fit = Math.max(MIN_CROWN_FIT, (fit * room) / need);
      c.style.setProperty('--crown-fit', String(fit));
    }
  }

  nameOn() {
    return Boolean(this.nameToggle && this.nameToggle.checked && this.addonId);
  }

  showNameError(msg) {
    if (!this.nameError) return;
    this.nameError.textContent = msg;
    this.nameError.hidden = !msg;
    this.nameInput?.setAttribute('aria-invalid', msg ? 'true' : 'false');
  }

  updatePrice() {
    if (!this.priceEl) return;
    if (this.nameOn() && this.pairCents) {
      const total = (this.pairCents + this.addonCents) / 100;
      const currency = window.Shopify?.currency?.active || 'USD';
      const digits = Number.isInteger(total) ? 0 : 2; // whole-dollar prices show as "$20", like the server-rendered price
      this.priceEl.textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(total);
    } else if (this.pairPrice) {
      this.priceEl.textContent = this.pairPrice;
    }
  }

  async buyNow() {
    if (!this.form || this.busy || !this.variantOk) return;
    if (this.nameOn()) return this.addWithName('checkout', this.buyNowBtn);
    this.setBusy(true, this.buyNowBtn);
    const root = window.Shopify?.routes?.root || '/';
    // The form holds the selected variant id + quantity 1: one frame per add.
    const data = new FormData(this.form);
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data
      });
      if (!res.ok) throw new Error('add failed');
      // Stay locked while the browser leaves for checkout.
      window.location.href = root + 'checkout';
    } catch (e) {
      // Fallback: the plain form post (lands on the cart). submit() skips the
      // submit listener, so the lock above does not block it.
      this.form.submit();
    }
  }

  async addWithName(dest, source) {
    const name = FrameConfigurator.cleanName(this.nameInput?.value).trim();
    if (!/^[A-Z][A-Z ]{0,9}$/.test(name)) {
      this.showNameError('Type a name of up to 10 letters, or untick "add a name".');
      this.nameInput?.focus();
      return;
    }
    this.setBusy(true, source);
    const root = window.Shopify?.routes?.root || '/';
    const items = [
      { id: Number(this.variantInput.value), quantity: 1 },
      { id: Number(this.addonId), quantity: 1, properties: { Name: name, 'Frame colour': this.pairLabel } }
    ];
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('add failed');
      window.location.href = root + (dest === 'checkout' ? 'checkout' : 'cart');
    } catch (e) {
      this.setBusy(false);
      this.showNameError('Something went wrong. Please try again.');
    }
  }

  setBusy(busy, source) {
    this.busy = busy;
    clearTimeout(this.unlockTimer);
    if (busy) this.unlockTimer = setTimeout(() => this.setBusy(false), UNLOCK_MS);
    [this.buyNowBtn, this.addBtn].forEach((b) => {
      if (!b) return;
      // Keep the section's label once, so every unlock can put it back.
      if (b.dataset.label === undefined) b.dataset.label = b.textContent.trim();
      if (busy && b === source) {
        b.setAttribute('aria-busy', 'true');
        b.textContent = BUSY_LABEL;
      } else {
        b.removeAttribute('aria-busy');
        b.textContent = b.dataset.label;
      }
    });
    this.syncButtons();
  }

  syncButtons() {
    const locked = this.busy || !this.variantOk;
    [this.buyNowBtn, this.addBtn].forEach((b) => {
      if (b) b.disabled = locked;
    });
  }

  setPressed(group, target) {
    group.forEach((b) => b.setAttribute('aria-pressed', b === target ? 'true' : 'false'));
  }
}

customElements.define('frame-configurator', FrameConfigurator);
